/*
  MIT License http://www.opensource.org/licenses/mit-license.php
  Author Tobias Koppers @sokra and Zackary Jackson @ScriptedAlchemy
*/

'use strict';
import type { Compiler } from 'webpack';
import { isRequiredVersion } from '@module-federation/sdk';
import type {
  sharePlugin,
  moduleFederationPlugin,
  consumeSharedPlugin,
  provideSharedPlugin,
} from '@module-federation/sdk';
type SharedConfig = moduleFederationPlugin.SharedConfig;
type ConsumesConfig = consumeSharedPlugin.ConsumesConfig;
type ProvidesConfig = provideSharedPlugin.ProvidesConfig;
import { parseOptions } from '../container/options';
import ConsumeSharedPlugin from './ConsumeSharedPlugin';
import ProvideSharedPlugin from './ProvideSharedPlugin';
import { getWebpackPath } from '@module-federation/sdk/normalize-webpack-path';
import { createSchemaValidation } from '../../utils';

const validate = createSchemaValidation(
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  require('../../schemas/sharing/SharePlugin.check.js').validate,
  () => require('../../schemas/sharing/SharePlugin').default,
  {
    name: 'Share Plugin',
    baseDataPath: 'options',
  },
);

export type NormalizedSharedOptions = [string, SharedConfig][];

export function normalizeSharedOptions(
  shared: moduleFederationPlugin.Shared,
): NormalizedSharedOptions {
  return parseOptions(
    shared,
    (item, key) => {
      if (typeof item !== 'string')
        throw new Error(
          `Unexpected array in shared configuration for key "${key}"`,
        );
      const config: SharedConfig =
        item === key || !isRequiredVersion(item)
          ? {
              import: item,
            }
          : {
              import: key,
              requiredVersion: item,
            };
      return config;
    },
    (item) => item,
  );
}

export function createConsumeShareOptions(
  normalizedSharedOptions: NormalizedSharedOptions,
) {
  return normalizedSharedOptions.map(([key, options]) => ({
    [key]: {
      import: options.import,
      shareKey: options.shareKey || key,
      shareScope: options.shareScope,
      requiredVersion: options.requiredVersion,
      strictVersion: options.strictVersion,
      singleton: options.singleton,
      packageName: options.packageName,
      eager: options.eager,
      issuerLayer: options.issuerLayer,
      layer: options.layer,
      request: options.request || key,
      exclude: options.exclude,
      include: options.include,
      allowNodeModulesSuffixMatch: options.allowNodeModulesSuffixMatch,
      treeShakingMode: options.treeShaking?.mode,
    },
  }));
}

export function createProvideShareOptions(
  normalizedSharedOptions: NormalizedSharedOptions,
) {
  return normalizedSharedOptions
    .filter(([, options]) => options.import !== false)
    .map(([key, options]) => ({
      [options.import || key]: {
        shareKey: options.shareKey || key,
        shareScope: options.shareScope,
        version: options.version,
        eager: options.eager,
        requiredVersion: options.requiredVersion,
        strictVersion: options.strictVersion,
        singleton: options.singleton,
        layer: options.layer,
        request: options.request || options.import || key,
        exclude: options.exclude,
        include: options.include,
        allowNodeModulesSuffixMatch: options.allowNodeModulesSuffixMatch,
        treeShakingMode: options.treeShaking?.mode,
      },
    }));
}

class SharePlugin {
  private _shareScope: string | string[];
  private _consumes: Record<string, ConsumesConfig>[];
  private _provides: Record<string, ProvidesConfig>[];

  constructor(options: sharePlugin.SharePluginOptions) {
    validate(options);

    const sharedOptions: [string, SharedConfig][] = normalizeSharedOptions(
      options.shared,
    );
    const consumes: Record<string, ConsumesConfig>[] =
      createConsumeShareOptions(sharedOptions);
    const provides: Record<string, ProvidesConfig>[] =
      createProvideShareOptions(sharedOptions);

    this._shareScope = options.shareScope || 'default';
    this._consumes = consumes;
    this._provides = provides;
  }

  /**
   * Applies the plugin to the webpack compiler instance
   * @param compiler - The webpack compiler instance
   */
  apply(compiler: Compiler): void {
    process.env['FEDERATION_WEBPACK_PATH'] =
      process.env['FEDERATION_WEBPACK_PATH'] || getWebpackPath(compiler);
    new ConsumeSharedPlugin({
      shareScope: this._shareScope,
      consumes: this._consumes,
    }).apply(compiler);

    new ProvideSharedPlugin({
      shareScope: this._shareScope,
      provides: this._provides,
    }).apply(compiler);
  }
}

export default SharePlugin;
